import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import usePageTitle from "../lib/usePageTitle";

/**
 * v2.71.13 — Printable HTML route for shortlist bundles.
 * Loads via the same public slug endpoint, renders a print-optimized layout,
 * and auto-fires window.print() after the data arrives + images settle.
 *
 * No backend PDF library — browser handles PDF via "Save as PDF" in print dialog.
 * Route: /shortlist/:slug/print
 */
export default function ShortlistPrint() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  usePageTitle("Shortlist | Print | HouseEntry");

  useEffect(() => {
    let alive = true;
    api
      .get(`/shortlists/${slug}`)
      .then((r) => {
        if (!alive) return;
        setData(r.data);
      })
      .catch((e) => {
        if (alive) setErr(e?.response?.data?.detail || "Not found");
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  // Fire the print dialog once content + images are loaded
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => {
      try {
        window.print();
      } catch (_) {
        /* silent */
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [data]);

  if (err) return <div className="p-10 text-center text-zinc-600">{err}</div>;
  if (!data) return <div className="p-10 text-center text-zinc-400">Loading shortlist…</div>;

  const { shortlist, properties, brand } = data;

  return (
    <div className="bg-white text-zinc-900 max-w-[820px] mx-auto p-8 print:p-0" data-testid="shortlist-print-root">
      <style>{`
        @media print {
          body { margin: 0; }
          .no-print { display: none !important; }
          .print-card { page-break-inside: avoid; }
          @page { margin: 14mm; }
        }
      `}</style>

      {/* Brand header */}
      <header className="border-b-2 border-zinc-900 pb-4 mb-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
              {brand?.name || "HouseEntry"}
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-0.5" data-testid="shortlist-print-title">
              {shortlist.name}
            </h1>
            {shortlist.client_name && (
              <div className="text-[13px] text-zinc-600 mt-0.5">
                Prepared for <span className="font-medium text-zinc-900">{shortlist.client_name}</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Properties</div>
            <div className="text-2xl font-bold">{properties.length}</div>
          </div>
        </div>
        {shortlist.notes && (
          <div className="mt-3 text-[12px] text-zinc-700 italic border-l-2 border-zinc-300 pl-3">
            {shortlist.notes}
          </div>
        )}
      </header>

      {/* Property list */}
      <div className="space-y-5">
        {properties.length === 0 && (
          <div className="text-center text-zinc-400 py-8 text-[13px]">No properties in this shortlist.</div>
        )}
        {properties.map((p, i) => {
          const img =
            (p.images && (typeof p.images[0] === "string" ? p.images[0] : p.images[0]?.url)) || "";
          return (
            <div
              key={p.id || i}
              className="print-card border border-zinc-300 rounded-sm p-4 flex gap-4"
              data-testid={`print-prop-${i}`}
            >
              <div className="w-32 h-24 bg-zinc-100 rounded-sm overflow-hidden flex-shrink-0">
                {img ? (
                  <img src={img} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-zinc-400">
                    No image
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                  #{i + 1}
                </div>
                <div className="font-semibold text-[15px] text-zinc-900 leading-tight">
                  {p.title}
                  {p.is_snapshot && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-orange-600 font-semibold">
                      (archived)
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-zinc-600 mt-0.5">
                  {p.area}
                  {p.area && p.city ? ", " : ""}
                  {p.city}
                </div>
                <div className="font-bold text-lg text-zinc-900 mt-1">
                  {p.price ? formatINR(p.price) : "Price on request"}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-300 mt-8 pt-3 text-[10px] text-zinc-500 flex justify-between">
        <span>
          {brand?.name || "HouseEntry"} • {brand?.support_email || ""}
        </span>
        <span>Generated {new Date().toLocaleDateString("en-IN")}</span>
      </footer>

      <div className="no-print text-center mt-6">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-zinc-900 text-white rounded-sm text-[12px] hover:bg-zinc-800"
          data-testid="shortlist-print-button"
        >
          Print / Save as PDF
        </button>
      </div>
    </div>
  );
}
